import { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { useLang } from '../lib/lang';
import { CONTACT, ADDRESS_LABELS } from '../content/constants';
import { Page, HeroShell, Reveal, Overline, VoltButton } from '../components/motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  'w-full bg-surface border border-white/15 px-4 py-3.5 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-volt focus:outline-none transition-colors duration-300';
const labelCls = 'block font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2';

export default function Contact() {
  const { t, lang } = useLang();
  const c = t.contact;
  const al = ADDRESS_LABELS[lang] || ADDRESS_LABELS.pt;
  const [form, setForm] = useState({ name: '', company: '', sector: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <Page meta={{ ...t.meta.contact, lang: t.lang }}>
      <HeroShell testId="contact-hero" overline={c.hero.overline} lines={c.hero.lines} sub={c.hero.sub} />

      <section data-testid="contact-body" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Overline>{c.form.overline}</Overline>
            {status === 'success' ? (
              <Reveal className="mt-10 border border-volt/40 bg-surface p-10 md:p-14" >
                <div data-testid="contact-success" className="flex items-start gap-4">
                  <CheckCircle2 className="w-8 h-8 text-volt shrink-0" />
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-white">{c.form.successTitle}</h2>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{c.form.successBody}</p>
                  </div>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="mt-10 grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cf-name" className={labelCls}>{c.form.name} *</label>
                  <input id="cf-name" data-testid="contact-form-name-input" required value={form.name} onChange={set('name')} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-company" className={labelCls}>{c.form.company}</label>
                  <input id="cf-company" data-testid="contact-form-company-input" value={form.company} onChange={set('company')} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-sector" className={labelCls}>{c.form.sector}</label>
                  <select id="cf-sector" data-testid="contact-form-sector-select" value={form.sector} onChange={set('sector')} className={inputCls}>
                    <option value="">—</option>
                    {c.form.sectors.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-phone" className={labelCls}>{c.form.phone}</label>
                  <input id="cf-phone" data-testid="contact-form-phone-input" type="tel" value={form.phone} onChange={set('phone')} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className={labelCls}>{c.form.message} *</label>
                  <textarea id="cf-message" data-testid="contact-form-message-input" required rows={5} value={form.message} onChange={set('message')} className={inputCls} />
                </div>
                {status === 'error' && (
                  <div data-testid="contact-error" className="sm:col-span-2 flex items-start gap-3 border border-red-500/40 bg-red-500/5 p-4">
                    <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
                    <p className="text-sm text-red-300">{c.form.errorBody}</p>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <VoltButton type="submit" testId="contact-form-submit-button">
                    {status === 'sending' ? c.form.sending : c.form.submit}
                  </VoltButton>
                </div>
              </form>
            )}
          </div>

          <div className="lg:col-span-5 space-y-12">
            <Reveal>
              <Overline>{c.direct.overline}</Overline>
              <div className="mt-6 border border-volt/25 bg-surface p-8" data-testid="contact-direct-block">
                <p className="font-display text-2xl md:text-3xl font-black tracking-tight text-white leading-tight">{c.direct.angolaTitle}</p>
                <p className="mt-2 font-mono text-sm md:text-base text-zinc-300">{CONTACT.company}</p>
                <div className="mt-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt">{al.hq}</p>
                  <p className="mt-2 font-display text-xl md:text-2xl font-bold text-white leading-snug">{CONTACT.hq.line1}</p>
                  <p className="mt-1 text-base text-zinc-300 leading-snug">{CONTACT.hq.line2}</p>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{al.tech}</p>
                  <p className="mt-1 text-sm text-zinc-400">{CONTACT.tech}</p>
                </div>
                <div className="mt-4 font-mono text-sm space-y-2">
                  <p><a href={`tel:${CONTACT.phoneHref}`} data-testid="contact-phone-link" className="text-white hover:text-volt transition-colors duration-300">{CONTACT.phone}</a></p>
                  <p><a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="text-white hover:text-volt transition-colors duration-300">{CONTACT.email}</a></p>
                  <p className="text-zinc-500 text-xs">NIF: {CONTACT.nif}</p>
                </div>
                <div className="mt-6 -mx-8 -mb-8 border-t border-white/10">
                  <iframe
                    title={lang === 'en' ? 'EDS Angola headquarters map' : 'Mapa da sede da EDS Angola'}
                    data-testid="contact-map"
                    src="https://www.google.com/maps?q=Edif%C3%ADcio%20KN10%2C%20-8.8227286%2C13.228253&z=17&output=embed"
                    className="w-full h-56 grayscale contrast-110 opacity-90 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <Overline>{c.group.overline}</Overline>
              <div className="mt-5 space-y-2.5">
                {t.footer.groupOffices.map((o) => (
                  <div key={o.country} className="border border-white/10 px-4 py-3" data-testid={`contact-office-${o.country.toLowerCase()}`}>
                    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-300">{o.country} <span className="text-zinc-600 font-normal normal-case tracking-normal">— {o.name}</span></p>
                    <p className="mt-1 font-mono text-[11px] text-zinc-500 leading-relaxed">{o.detail}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </Page>
  );
}
